import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import Header from '../components/Header';
import FoodCard from '../components/FoodCard';
import BottomNavigation from '../components/BottomNavigation';
import Drawer from '../components/Drawer';
import CartDrawer from '../components/CartDrawer';
import FoodDetailsDrawer from '../components/FoodDetailsDrawer';
import ProfileDrawer from '../components/ProfileDrawer';
import { foodItems, categories } from '../data/mockData';
import { FoodItem } from '../types';
import { colors, spacing } from '../styles/theme';
import { useCart } from '../hooks/useCart';

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${colors.background};
  padding-bottom: 80px; // Space for bottom navigation
`;

const Content = styled.div`
  padding: 0 ${spacing.lg};
  padding-top: ${spacing.md};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${spacing.lg};
  margin-bottom: ${spacing.xl};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${spacing.xxxl};
  color: ${colors.textTertiary};
`;

const FilterDrawer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
`;

const FilterTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.textPrimary};
  margin: 0;
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
`;

const Checkbox = styled.label`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  cursor: pointer;
`;

const CheckboxInput = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${colors.primary};
`;

const CheckboxLabel = styled.span`
  font-size: 14px;
  color: ${colors.textSecondary};
`;

const MenuPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  const [filters, setFilters] = useState({
    vegetarian: false,
    vegan: false,
    glutenFree: false,
  });
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);
  const [selectedFoodItem, setSelectedFoodItem] = useState<FoodItem | null>(null);
  const [isFoodDetailsOpen, setIsFoodDetailsOpen] = useState(false);
  const [isProfileDrawerOpen, setIsProfileDrawerOpen] = useState(false);

  const { addToCart, getCartItemsCount, cartItems, total, removeFromCart, updateQuantity } = useCart();

  const filteredItems = useMemo(() => {
    return foodItems.filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          item.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
      const matchesFilters = (!filters.vegetarian || item.isVegetarian) &&
                           (!filters.vegan || item.isVegan) &&
                           (!filters.glutenFree || item.isGlutenFree);

      return matchesSearch && matchesCategory && matchesFilters;
    });
  }, [searchQuery, selectedCategory, filters]);

  const handleFoodCardClick = (item: FoodItem) => {
    setSelectedFoodItem(item);
    setIsFoodDetailsOpen(true);
  };

  const handleAddToCartWithModifiers = (item: FoodItem, selectedIngredients: string[], quantity: number) => {
    for (let i = 0; i < quantity; i++) {
      addToCart(item);
    }
    // Здесь можно сохранить информацию о выбранных ингредиентах в будущем
    console.log('Added to cart:', item.name, 'with ingredients:', selectedIngredients, 'quantity:', quantity);
  };

  const handleOrder = () => {
    // В будущем здесь будет логика оформления заказа
    alert(`Заказ на сумму ${total}₽ успешно оформлен!`);
    setIsCartDrawerOpen(false);
  };

  const handleFilterChange = (filterName: string, checked: boolean) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: checked
    }));
  };

  return (
    <PageContainer>
      <Header
        title="Меню Korzilla"
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        onFilterClick={() => setIsFilterDrawerOpen(true)}
        categories={categories}
      />

      <Content>
        {filteredItems.length > 0 ? (
          <Grid>
            {filteredItems.map(item => (
              <FoodCard
                key={item.id}
                item={item}
                onClick={() => handleFoodCardClick(item)}
              />
            ))}
          </Grid>
        ) : (
          <EmptyState>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🍽️</div>
            <h3>Ничего не найдено</h3>
            <p>Попробуйте изменить критерии поиска</p>
          </EmptyState>
        )}
      </Content>

      <BottomNavigation
        activeTab={activeTab}
        onTabChange={(tab) => {
          setActiveTab(tab);
          if (tab === 'cart') {
            setIsCartDrawerOpen(true);
          } else if (tab === 'profile') {
            setIsProfileDrawerOpen(true);
          }
          // Здесь можно добавить обработку других табов в будущем
        }}
        cartItemsCount={getCartItemsCount()}
      />

      <Drawer
        isOpen={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        title="Фильтры"
        position="right"
      >
        <FilterDrawer>
          <FilterSection>
            <FilterTitle>Диетические предпочтения</FilterTitle>
            <CheckboxGroup>
              <Checkbox>
                <CheckboxInput
                  type="checkbox"
                  checked={filters.vegetarian}
                  onChange={(e) => handleFilterChange('vegetarian', e.target.checked)}
                />
                <CheckboxLabel>Вегетарианское</CheckboxLabel>
              </Checkbox>
              <Checkbox>
                <CheckboxInput
                  type="checkbox"
                  checked={filters.vegan}
                  onChange={(e) => handleFilterChange('vegan', e.target.checked)}
                />
                <CheckboxLabel>Веганское</CheckboxLabel>
              </Checkbox>
              <Checkbox>
                <CheckboxInput
                  type="checkbox"
                  checked={filters.glutenFree}
                  onChange={(e) => handleFilterChange('glutenFree', e.target.checked)}
                />
                <CheckboxLabel>Без глютена</CheckboxLabel>
              </Checkbox>
            </CheckboxGroup>
          </FilterSection>
        </FilterDrawer>
      </Drawer>

      <Drawer
        isOpen={isCartDrawerOpen}
        onClose={() => setIsCartDrawerOpen(false)}
        title="Корзина"
        position="bottom"
      >
        <CartDrawer
          cartItems={cartItems}
          total={total}
          onUpdateQuantity={updateQuantity}
          onRemoveItem={removeFromCart}
          onOrder={handleOrder}
        />
      </Drawer>

      {selectedFoodItem && (
        <FoodDetailsDrawer
          foodItem={selectedFoodItem}
          isOpen={isFoodDetailsOpen}
          onClose={() => {
            setIsFoodDetailsOpen(false);
            setSelectedFoodItem(null);
          }}
          onAddToCart={handleAddToCartWithModifiers}
        />
      )}

      <ProfileDrawer
        isOpen={isProfileDrawerOpen}
        onClose={() => setIsProfileDrawerOpen(false)}
      />

    </PageContainer>
  );
};

export default MenuPage;
